import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  type ReportDashboardMetricOptions,
} from '@core/reports-types';

/**
 * Net change results always carry three value columns in a fixed order, right
 * after the index dimension: the positive metric (e.g. joiners), the negative
 * one (leavers) - both as positive counts - and the backend computed net.
 * Direction is driven purely by column order, the same way trend and breakdown
 * already map their series.
 */
export const NET_CHANGE_POSITIVE_COLUMN = 1;
export const NET_CHANGE_NEGATIVE_COLUMN = 2;
export const NET_CHANGE_NET_COLUMN = 3;

const NET_CHANGE_CHART_TYPES: string[] = [
  ReportDashboardComponentChartType.Bar,
  ReportDashboardComponentChartType.Column,
];

export const isNetChangeMetric = (metric: Pick<ReportDashboardMetricOptions, 'kind'>): boolean =>
  metric.kind === ReportDashboardComponentKind.NetChange;

/**
 * Net change comes in two flavours sharing one kind: bucketed over time (the x
 * axis is the abstract `period` date field) and split by a dimension. Only the
 * former is bucketed by an interval, so only it gets a frequency control.
 */
export const isNetChangeOverTime = (metric: ReportDashboardMetricOptions): boolean =>
  isNetChangeMetric(metric) &&
  metric.availableDateFields.some(field => field.isAbstract && field.id === metric.xAxisId);

/**
 * Chart types the frontend can actually render for a metric. Net change only
 * supports diverging column (over time) and back-to-back bar (by dimension);
 * anything else the backend lists would resolve to no visualization at all.
 */
export const getSupportedChartTypes = (metric: ReportDashboardMetricOptions): string[] => {
  if (!isNetChangeMetric(metric)) {
    return metric.availableChartTypes;
  }
  const supported = metric.availableChartTypes.filter(type =>
    NET_CHANGE_CHART_TYPES.includes(type),
  );
  return supported.length
    ? supported
    : [
        isNetChangeOverTime(metric)
          ? ReportDashboardComponentChartType.Column
          : ReportDashboardComponentChartType.Bar,
      ];
};
